/**
 * 학습 기록 / AI 학습 패널 크기를 localStorage에 저장하고 불러옵니다.
 * 최대 크기는 화면(뷰포트) 크기에 따라 동적으로 적용됩니다.
 */
const STORAGE_KEY_PANEL_HEIGHT = 'bookViewer_panelHeight';
const STORAGE_KEY_PANEL_BOTTOM = 'bookViewer_panelBottom';
const STORAGE_KEY_LEARNING_RECORDS_WIDTH = 'bookViewer_learningRecordsWidth';
const STORAGE_KEY_LEARNING_RECORDS_LEFT = 'bookViewer_learningRecordsLeft';
const STORAGE_KEY_AI_PANEL_WIDTH = 'bookViewer_aiPanelWidth';
const STORAGE_KEY_AI_PANEL_RIGHT = 'bookViewer_aiPanelRight';
const STORAGE_KEY_ATTACHMENT_PANEL_ON_LEFT = 'bookViewer_attachmentPanelOnLeft';

const DEFAULT_HEIGHT = 780;
const DEFAULT_BOTTOM = 20;
const DEFAULT_LEARNING_RECORDS_WIDTH = 406;
const DEFAULT_LEARNING_RECORDS_LEFT = 20;
const DEFAULT_AI_PANEL_WIDTH = 560;
const DEFAULT_AI_PANEL_RIGHT = 20;

const MIN_HEIGHT = 300;
const MAX_HEIGHT = 900;
const MIN_BOTTOM = 0;
const MIN_LEARNING_RECORDS_WIDTH = 280;
const MIN_LEARNING_RECORDS_LEFT = 0;
const MIN_AI_PANEL_WIDTH = 360;
const MIN_AI_PANEL_RIGHT = 0;

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/** 뷰포트 크기 기반 동적 최대값 반환 */
const HEADER_HEIGHT = 72;
const MOBILE_BREAKPOINT = 768;
const TABLET_BREAKPOINT = 1024;

export function getPanelSizeLimits(viewportWidth?: number, viewportHeight?: number) {
  const vw = viewportWidth ?? (typeof window !== 'undefined' ? window.innerWidth : 1920);
  const vh = viewportHeight ?? (typeof window !== 'undefined' ? window.innerHeight : 1080);
  const margin = HEADER_HEIGHT + 20;
  const isMobile = vw <= MOBILE_BREAKPOINT;
  const isTablet = vw > MOBILE_BREAKPOINT && vw <= TABLET_BREAKPOINT;

  // 모바일: 화면 비율에 맞게 유동적으로
  if (isMobile) {
    const mobileMinHeight = Math.max(200, Math.round(vh * 0.3));
    const mobileMaxHeight = Math.max(mobileMinHeight, Math.round(vh * 0.7));
    const mobileMinWidth = Math.max(200, Math.round(vw * 0.5));
    return {
      minHeight: mobileMinHeight,
      maxHeight: mobileMaxHeight,
      minBottom: MIN_BOTTOM,
      maxBottom: Math.max(MIN_BOTTOM, vh - mobileMinHeight - 20),
      minLearningRecordsWidth: mobileMinWidth,
      maxLearningRecordsWidth: Math.max(mobileMinWidth, vw - 40),
      minLearningRecordsLeft: 10,
      maxLearningRecordsLeft: Math.max(10, vw - mobileMinWidth - 10),
      minAIPanelWidth: mobileMinWidth,
      maxAIPanelWidth: Math.max(mobileMinWidth, vw - 40),
      minAIPanelRight: 10,
      maxAIPanelRight: Math.max(10, vw - mobileMinWidth - 10),
    };
  }

  // 태블릿: PC와 동일한 고정값 사용, 최대값만 화면에 맞게 조정
  if (isTablet) {
    return {
      minHeight: MIN_HEIGHT,
      maxHeight: Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, vh - margin)),
      minBottom: MIN_BOTTOM,
      maxBottom: Math.max(MIN_BOTTOM, vh - MIN_HEIGHT - 20),
      minLearningRecordsWidth: MIN_LEARNING_RECORDS_WIDTH,
      maxLearningRecordsWidth: Math.max(MIN_LEARNING_RECORDS_WIDTH, vw - margin - MIN_AI_PANEL_WIDTH - 20),
      minLearningRecordsLeft: MIN_LEARNING_RECORDS_LEFT,
      maxLearningRecordsLeft: Math.max(MIN_LEARNING_RECORDS_LEFT, vw - margin - MIN_LEARNING_RECORDS_WIDTH),
      minAIPanelWidth: MIN_AI_PANEL_WIDTH,
      maxAIPanelWidth: Math.max(MIN_AI_PANEL_WIDTH, vw - margin - MIN_LEARNING_RECORDS_WIDTH - 20),
      minAIPanelRight: MIN_AI_PANEL_RIGHT,
      maxAIPanelRight: Math.max(MIN_AI_PANEL_RIGHT, vw - MIN_AI_PANEL_WIDTH - 20),
    };
  }

  // PC: 고정 최소/최대값 사용
  return {
    minHeight: MIN_HEIGHT,
    maxHeight: Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, vh - margin)),
    minBottom: MIN_BOTTOM,
    maxBottom: Math.max(MIN_BOTTOM, vh - MIN_HEIGHT - 20),
    minLearningRecordsWidth: MIN_LEARNING_RECORDS_WIDTH,
    maxLearningRecordsWidth: Math.max(MIN_LEARNING_RECORDS_WIDTH, vw - margin - MIN_AI_PANEL_WIDTH - 20),
    minLearningRecordsLeft: MIN_LEARNING_RECORDS_LEFT,
    maxLearningRecordsLeft: Math.max(MIN_LEARNING_RECORDS_LEFT, vw - margin - MIN_LEARNING_RECORDS_WIDTH),
    minAIPanelWidth: MIN_AI_PANEL_WIDTH,
    maxAIPanelWidth: Math.max(MIN_AI_PANEL_WIDTH, vw - margin - MIN_LEARNING_RECORDS_WIDTH - 20),
    minAIPanelRight: MIN_AI_PANEL_RIGHT,
    maxAIPanelRight: Math.max(MIN_AI_PANEL_RIGHT, vw - MIN_AI_PANEL_WIDTH - 20),
  };
}

export function getStoredPanelHeight(): number {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_PANEL_HEIGHT);
    if (stored) {
      const num = parseInt(stored, 10);
      if (!isNaN(num) && num >= 200) return num;
    }
  } catch (_) {}
  // 모바일에서는 화면 비율에 맞는 기본값 반환
  if (typeof window !== 'undefined' && window.innerWidth <= MOBILE_BREAKPOINT) {
    return Math.round(window.innerHeight * 0.55);
  }
  return DEFAULT_HEIGHT;
}

export function setStoredPanelHeight(value: number): void {
  try {
    const v = Math.max(MIN_HEIGHT, value);
    localStorage.setItem(STORAGE_KEY_PANEL_HEIGHT, String(v));
  } catch (_) {}
}

export function getStoredPanelBottom(): number {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_PANEL_BOTTOM);
    if (stored) {
      const num = parseInt(stored, 10);
      if (!isNaN(num) && num >= MIN_BOTTOM) return num;
    }
  } catch (_) {}
  return DEFAULT_BOTTOM;
}

export function setStoredPanelBottom(value: number): void {
  try {
    const v = Math.max(MIN_BOTTOM, value);
    localStorage.setItem(STORAGE_KEY_PANEL_BOTTOM, String(v));
  } catch (_) {}
}

export function getStoredLearningRecordsWidth(): number {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_LEARNING_RECORDS_WIDTH);
    if (stored) {
      const num = parseInt(stored, 10);
      if (!isNaN(num) && num >= 200) return num;
    }
  } catch (_) {}
  // 모바일에서는 화면 비율에 맞는 기본값 반환
  if (typeof window !== 'undefined' && window.innerWidth <= MOBILE_BREAKPOINT) {
    return Math.round(window.innerWidth * 0.9);
  }
  //return Math.round(window.innerWidth * 0.24);
  return DEFAULT_LEARNING_RECORDS_WIDTH;
}

export function setStoredLearningRecordsWidth(value: number): void {
  try {
    const v = Math.max(MIN_LEARNING_RECORDS_WIDTH, value);
    localStorage.setItem(STORAGE_KEY_LEARNING_RECORDS_WIDTH, String(v));
  } catch (_) {}
}

export function getStoredLearningRecordsLeft(): number {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_LEARNING_RECORDS_LEFT);
    if (stored) {
      const num = parseInt(stored, 10);
      if (!isNaN(num) && num >= MIN_LEARNING_RECORDS_LEFT) return num;
    }
  } catch (_) {}
  return DEFAULT_LEARNING_RECORDS_LEFT;
}

export function setStoredLearningRecordsLeft(value: number): void {
  try {
    const v = Math.max(MIN_LEARNING_RECORDS_LEFT, value);
    localStorage.setItem(STORAGE_KEY_LEARNING_RECORDS_LEFT, String(v));
  } catch (_) {}
}

export function getStoredAIPanelWidth(): number {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_AI_PANEL_WIDTH);
    if (stored) {
      const num = parseInt(stored, 10);
      if (!isNaN(num) && num >= 200) return num;
    }
  } catch (_) {}
  // 모바일에서는 화면 비율에 맞는 기본값 반환
  if (typeof window !== 'undefined' && window.innerWidth <= MOBILE_BREAKPOINT) {
    return Math.round(window.innerWidth * 0.9);
  }
  //return Math.round(window.innerWidth * 0.74);
  return DEFAULT_AI_PANEL_WIDTH;
}

export function setStoredAIPanelWidth(value: number): void {
  try {
    const v = Math.max(MIN_AI_PANEL_WIDTH, value);
    localStorage.setItem(STORAGE_KEY_AI_PANEL_WIDTH, String(v));
  } catch (_) {}
}

export function getStoredAIPanelRight(): number {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_AI_PANEL_RIGHT);
    if (stored) {
      const num = parseInt(stored, 10);
      if (!isNaN(num) && num >= MIN_AI_PANEL_RIGHT) return num;
    }
  } catch (_) {}
  return DEFAULT_AI_PANEL_RIGHT;
}

export function setStoredAIPanelRight(value: number): void {
  try {
    const v = Math.max(MIN_AI_PANEL_RIGHT, value);
    localStorage.setItem(STORAGE_KEY_AI_PANEL_RIGHT, String(v));
  } catch (_) {}
}

export function getStoredAttachmentPanelOnLeft(): boolean {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_ATTACHMENT_PANEL_ON_LEFT);
    if (stored === 'false') return false;
    if (stored === 'true') return true;
  } catch (_) {}
  return true;
}

export function setStoredAttachmentPanelOnLeft(value: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY_ATTACHMENT_PANEL_ON_LEFT, String(value));
  } catch (_) {}
}

